import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { createSignedWebSocketUrl } from 'utils/createSignedWebSocketUrl';
import { useWebSocket } from './useWebSocket';
import {
  addEmailMessage,
  addUnreadMessage,
  setNotifyEmailMessage,
} from 'store/projectEmail/projectEmailSlice';

/**
 * Custom hook for the project email WebSocket, which pushes an email to the
 * projects it arrived for as it arrives.
 *
 * Nothing subscribes from here: the socket subscribes each connection itself,
 * on $connect, to the projects the signed-in user may read email in. This used
 * to send a frame per project naming the user and the project it wanted, which
 * is how anyone with the URL could subscribe to anyone's mail.
 *
 * @param {Object} user - Current user object
 * @returns {Object} - { emailWebsocketRef }
 */
export const useProjectEmailWebSocket = (user) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { wsRef: emailWebsocketRef } = useWebSocket({
    url: process.env.REACT_APP_PROJECT_EMAIL_WEBSOCKET_URL,
    // $connect is AWS_IAM, so every attempt is SigV4-signed afresh. The socket
    // before that change ignores the signature, so this works against both.
    getUrl: createSignedWebSocketUrl,
    enabled: !!user?.id,
    onMessage: (event) => {
      try {
        const message = JSON.parse(event.data);

        // Always increment unread count
        dispatch(addUnreadMessage({ projectKey: message.projectKey }));

        // Dispatch notification for the new email message
        dispatch(setNotifyEmailMessage(message));

        // Only add to message list if user is viewing this project's email
        const currentPath = history.location.pathname;
        const isViewingProjectEmail = currentPath.includes(
          `/project/${message.projectKey}/email`
        );

        if (isViewingProjectEmail) {
          dispatch(addEmailMessage({ emailMessage: message }));
        }
      } catch (error) {
        console.error('Error parsing project email WebSocket message:', error);
      }
    },
    onOpen: () => {
      window.emailWebsocketRef = emailWebsocketRef;
    },
    onClose: (event) => {
      delete window.emailWebsocketRef;
    },
    onError: (error) => {
      console.error('Project email WebSocket error:', error);
    },
  });

  return {
    emailWebsocketRef,
  };
};
